import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import type { WearableBodySummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import MetricValue from './MetricValue';

interface BodyMetric {
  key: keyof WearableBodySummary;
  label: string;
  unit?: string;
}

const BODY_METRICS: BodyMetric[] = [
  { key: 'weightKg', label: 'Weight', unit: 'kg' },
  { key: 'heightCm', label: 'Height', unit: 'cm' },
  { key: 'bodyFatPercent', label: 'Body fat', unit: '%' },
  { key: 'muscleMassKg', label: 'Muscle mass', unit: 'kg' },
  { key: 'bmi', label: 'BMI' },
  { key: 'age', label: 'Age' },
  { key: 'restingHeartRateBpm', label: 'Resting heart rate', unit: 'bpm' },
  { key: 'avgHrvSdnnMs', label: 'HRV (avg)', unit: 'ms' },
  { key: 'avgPeriodDays', label: 'Avg cycle length', unit: 'days' },
  { key: 'bodyTemperatureCelsius', label: 'Body temperature', unit: '°C' },
  { key: 'skinTemperatureCelsius', label: 'Skin temperature', unit: '°C' },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  bodySummary: WearableBodySummary | null;
}

const BodySummarySection: React.FC<Props> = ({ range, loading, error, bodySummary }) => {
  return (
    <Card className="mb-4" data-testid="body-summary-section">
      <CardHeader>
        Body
        {!range.isSingleDay && bodySummary ? (
          <small className="ms-2 text-muted">(last known value)</small>
        ) : null}
      </CardHeader>
      <CardBody>
        {loading && !bodySummary ? <Spinner /> : null}
        {error ? (
          <div data-testid="body-summary-error" className="text-danger">
            Could not load section: {error.message}
          </div>
        ) : null}
        {!loading && !error && !bodySummary ? (
          <div data-testid="body-summary-empty">No data for the selected day</div>
        ) : null}
        {bodySummary
          ? BODY_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                label={metric.label}
                unit={metric.unit}
                value={bodySummary[metric.key] as number | null}
                provider={bodySummary.provider}
                device={bodySummary.device}
              />
            ))
          : null}
      </CardBody>
    </Card>
  );
};

export default BodySummarySection;
export { BODY_METRICS };
